import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { memo, useCallback, useRef, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import uniqueId from 'lodash/uniqueId';
import get from 'lodash/get';
import omit from 'lodash/omit';
import { useRegvar } from '../../hooks/useRegvar';
import ValidationGroupable from '../ValidationGroupable';
import { useI18n } from '../../i18n';
import { useIsFormReadOnly } from '../../hooks/useIsFormReadOnly';
import VanillaQuestion from './Vanilla';
import { useRegvarImport } from './Import';
const mapStateToProps = (state)=>{
    return {
        registerDocumentation: state.register?.regvars,
        showReviewerCheckbox: state.settings?.showReviewerCheckboxes ?? false,
        readOnlyProp: state.settings?.readOnlyProp || 'readOnly'
    };
};
const Question = (props)=>{
    const { regvar, showRegvarName, readOnly, ...rest } = props;
    const { t } = useI18n();
    const isFormReadOnly = useIsFormReadOnly();
    const { registerDocumentation, showReviewerCheckbox, readOnlyProp } = useSelector(mapStateToProps, shallowEqual);
    const [setValue, setInclude, setDirty] = useRegvar(regvar, props);
    const imp = useRegvarImport(regvar);
    const isValueImported = imp ? imp.isImported : undefined;
    const setImportedValue = imp ? imp.onImport : undefined;
    const importableValues = imp ? imp.importableValues : undefined;
    const propsRef = useRef(props);
    propsRef.current = props;
    const [id] = useState(()=>{
        return props.id || uniqueId(`Question_${props.regvar.$rcc.shortname}_`);
    });
    const onBlur = useCallback((...args)=>{
        if (propsRef.current.disabled || propsRef.current.isReadOnly) {
            return;
        }
        setDirty(true);
        if (propsRef.current.onBlur) {
            propsRef.current.onBlur(...args);
        }
    }, [
        propsRef,
        setDirty
    ]);
    const onValueChange = useCallback((value, ...other)=>{
        const currentRawValue = propsRef.current.regvar.value;
        if (propsRef.current.disabled || propsRef.current.isReadOnly) {
            return;
        }
        setValue(value);
        if (value !== currentRawValue && propsRef.current.onValueChange) {
            propsRef.current.onValueChange(value, ...other);
        }
    }, [
        propsRef,
        setValue
    ]);
    const onIncludeChange = useCallback((include)=>{
        const currentRawValue = propsRef.current.regvar.include;
        if (propsRef.current.isReadOnly) {
            return;
        }
        setInclude(include);
        if (include !== currentRawValue && propsRef.current.onIncludeChange) {
            propsRef.current.onIncludeChange(include);
        }
    }, [
        propsRef,
        setInclude
    ]);
    const valErrors = regvar.getValidationErrors().toJS();
    let errors = [];
    let warnings = [];
    if (regvar.$isDirty && valErrors.length) {
        errors = valErrors.filter((x)=>x.isFatal).map((x)=>/*#__PURE__*/ _jsx("div", {
                children: t(x.message, x.options)
            }, x.rule));
        warnings = valErrors.filter((x)=>!x.isFatal).map((x)=>/*#__PURE__*/ _jsx("div", {
                children: t(x.message, x.options)
            }, x.rule));
    }
    const extraProps = {
        [readOnlyProp]: isFormReadOnly || regvar.$rcc.regvarMetadata.serversideOnly || readOnly
    };
    // Needed. Otherwise readOnly state above can clear disabled value (if readOnlyProp = disabled).
    if (props.disabled) {
        extraProps.disabled = props.disabled;
    }
    const propsToPass = omit(rest, [
        'disableValidation'
    ]);
    return /*#__PURE__*/ _jsxs("div", {
        className: "question",
        children: [
            /*#__PURE__*/ _jsx(VanillaQuestion, {
                ...propsToPass,
                ...extraProps,
                id: id,
                value: regvar.value,
                include: regvar.include,
                onValueChange: onValueChange,
                onIncludeChange: onIncludeChange,
                isValueImported: isValueImported,
                onImportValueChange: setImportedValue,
                importableValues: importableValues,
                onBlur: onBlur,
                regvarMetadata: regvar.$rcc.regvarMetadata,
                showReviewerCheckbox: props.showReviewerCheckbox ?? showReviewerCheckbox,
                regvarRegisterDocumentation: get(registerDocumentation, regvar.$rcc.shortname),
                error: errors.length ? errors : null,
                warning: warnings.length ? warnings : null
            }),
            showRegvarName && /*#__PURE__*/ _jsx("p", {
                children: regvar.get('$rcc').shortname
            })
        ]
    });
};
const ValidationGroupableQuestion = (props)=>{
    const { validationGroup, ...rest } = props;
    return /*#__PURE__*/ _jsx(ValidationGroupable, {
        idPrefix: props.regvar.$rcc.shortname,
        errors: [],
        isDirty: props.regvar.$isDirty,
        validationGroup: validationGroup,
        children: /*#__PURE__*/ _jsx(Question, {
            ...rest
        })
    });
};
export default /*#__PURE__*/ memo(ValidationGroupableQuestion);
